// ============================================================
// RB-PMS v3 — Quotation Template Page
// File: src/pages/quotation/QuotationTemplatePage.tsx
//
// Flow:
//   1. User uploads an image of a quotation template
//   2. Image is sent to the backend, which asks Claude's vision
//      model to detect fillable fields (name, date, amount, etc.)
//   3. User reviews/edits the detected fields, then saves the
//      template
//   4. User can then create "entries" — fill in real values for
//      a saved template's fields — as many times as needed
// ============================================================

import React, { useState, useCallback, useEffect } from 'react';
import { Upload, Loader2, Plus, Trash2, Save, FileText, X, Image as ImageIcon, Download, Library, ArrowLeft } from 'lucide-react';
import { quotationTemplateService, QuotationField, QuotationTemplate } from '../../services/quotationTemplate.service';

type Step = 'browse' | 'upload' | 'reviewing' | 'saved';

const FIELD_TYPES: QuotationField['field_type'][] = ['text', 'number', 'date', 'textarea'];

const QuotationTemplatePage: React.FC = () => {
  const [step, setStep] = useState<Step>('browse');
  const [templates, setTemplates] = useState<QuotationTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fields, setFields] = useState<QuotationField[]>([]);
  const [imageData, setImageData] = useState<string>('');
  const [imageMimeType, setImageMimeType] = useState<string>('');
  const [templateName, setTemplateName] = useState('');

  // Fill-in form state, once a template is saved
  const [entryName, setEntryName] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [savedTemplateId, setSavedTemplateId] = useState<number | null>(null);
  const [lastSavedEntryId, setLastSavedEntryId] = useState<number | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleFileSelect = useCallback((file: File) => {
    setError(null);
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setError(null);
    try {
      const res = await quotationTemplateService.getAll();
      setTemplates(res.data.data);
    } catch (e: any) {
      setError('Failed to load saved templates.');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (step === 'browse') loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleUseTemplate = (template: QuotationTemplate) => {
    setFields(template.fields);
    setTemplateName(template.template_name);
    setSavedTemplateId(template.template_id);
    setFieldValues({});
    setEntryName('');
    setLastSavedEntryId(null);
    setError(null);
    setStep('saved');
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleExtract = async () => {
    if (!imageFile) return;
    setExtracting(true);
    setError(null);
    try {
      const res = await quotationTemplateService.extractFields(imageFile);
      const { fields: extractedFields, image_data, image_mime_type } = res.data.data;
      setFields(extractedFields);
      setImageData(image_data);
      setImageMimeType(image_mime_type);
      setTemplateName(imageFile.name.replace(/\.[^/.]+$/, ''));
      setStep('reviewing');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to extract fields. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  const updateField = (index: number, key: keyof QuotationField, value: string) => {
    setFields(prev => prev.map((f, i) => i === index ? { ...f, [key]: value } : f));
  };

  const removeField = (index: number) => {
    setFields(prev => prev.filter((_, i) => i !== index));
  };

  const addField = () => {
    setFields(prev => [...prev, { field_key: `field_${prev.length + 1}`, label: 'New Field', field_type: 'text' }]);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || fields.length === 0) {
      setError('Give the template a name and keep at least one field.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await quotationTemplateService.saveTemplate({
        template_name: templateName,
        image_data: imageData,
        image_mime_type: imageMimeType,
        fields
      });
      setSavedTemplateId(res.data.data.template_id);
      setFieldValues({});
      setStep('saved');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEntry = async () => {
    if (!savedTemplateId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await quotationTemplateService.createEntry(savedTemplateId, entryName, fieldValues);
      setLastSavedEntryId(res.data.data.entry_id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!lastSavedEntryId) return;
    setDownloadingPdf(true);
    setError(null);
    try {
      const res = await quotationTemplateService.downloadEntryPdf(lastSavedEntryId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quotation-${lastSavedEntryId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const resetAll = () => {
    setStep('browse');
    setImageFile(null);
    setImagePreviewUrl(null);
    setFields([]);
    setImageData('');
    setImageMimeType('');
    setTemplateName('');
    setFieldValues({});
    setEntryName('');
    setSavedTemplateId(null);
    setLastSavedEntryId(null);
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Quotation Template</h1>
        <p className="text-gray-500 text-sm mt-1">
          Upload a quotation template image — AI will detect the fillable fields for you.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}

      {/* ── STEP 0: BROWSE SAVED TEMPLATES ────────────────────── */}
      {step === 'browse' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <Library size={18} /> Saved Templates
            </h2>
            <button
              onClick={() => setStep('upload')}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus size={16} /> Upload New Template
            </button>
          </div>

          {loadingTemplates ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <FileText size={36} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm">No saved templates yet.</p>
              <p className="text-gray-400 text-xs mt-1">Upload a quotation image to create your first one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {templates.map(t => (
                <div key={t.template_id} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t.template_name}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {t.fields.length} field{t.fields.length === 1 ? '' : 's'} · Saved {new Date(t.created_at).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                    <FileText size={20} className="text-gray-300 flex-shrink-0" />
                  </div>
                  <button
                    onClick={() => handleUseTemplate(t)}
                    className="mt-3 w-full px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100"
                  >
                    Use This Template
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── STEP 1: UPLOAD ─────────────────────────────────────── */}
      {step === 'upload' && (
        <div>
          <button onClick={() => setStep('browse')} className="mb-4 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <ArrowLeft size={14} /> Back to Saved Templates
          </button>
        <div
          onDrop={onDrop}
          onDragOver={e => e.preventDefault()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center bg-white hover:border-blue-400 transition-colors"
        >
          {imagePreviewUrl ? (
            <div className="space-y-4">
              <img src={imagePreviewUrl} alt="preview" className="max-h-80 mx-auto rounded-lg shadow" />
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => { setImageFile(null); setImagePreviewUrl(null); }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-50"
                >
                  Choose Different Image
                </button>
                <button
                  onClick={handleExtract}
                  disabled={extracting}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {extracting ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {extracting ? 'Analyzing image...' : 'Extract Fields'}
                </button>
              </div>
            </div>
          ) : (
            <label className="cursor-pointer flex flex-col items-center gap-3">
              <ImageIcon size={40} className="text-gray-400" />
              <div>
                <p className="text-gray-700 font-medium">Drop a quotation image here, or click to browse</p>
                <p className="text-gray-400 text-xs mt-1">PNG, JPG up to 10MB</p>
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={onFileInputChange} />
            </label>
          )}
        </div>
        </div>
      )}

      {/* ── STEP 2: REVIEW EXTRACTED FIELDS ────────────────────── */}
      {step === 'reviewing' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
            <input
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="flex justify-between items-center mb-3">
            <h2 className="font-semibold text-gray-800">Detected Fields</h2>
            <button onClick={addField} className="text-sm text-blue-600 flex items-center gap-1 hover:underline">
              <Plus size={14} /> Add Field
            </button>
          </div>

          <div className="space-y-2 mb-6">
            {fields.map((field, i) => (
              <div key={i} className="flex gap-2 items-center bg-gray-50 rounded-lg p-2">
                <input
                  value={field.label}
                  onChange={e => updateField(i, 'label', e.target.value)}
                  placeholder="Field label"
                  className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                />
                <select
                  value={field.field_type}
                  onChange={e => updateField(i, 'field_type', e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                >
                  {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={() => removeField(i)} className="text-red-500 hover:text-red-700 p-1">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {fields.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">No fields yet — add one above.</p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={resetAll} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-50">
              Start Over
            </button>
            <button
              onClick={handleSaveTemplate}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save Template
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: FILL IN VALUES FOR A SAVED TEMPLATE ────────── */}
      {step === 'saved' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <button onClick={() => setStep('browse')} className="mb-4 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <ArrowLeft size={14} /> Back to Saved Templates
          </button>
          <div className="flex items-center gap-2 mb-4 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
            <FileText size={16} />
            Using template "{templateName}". Fill in the details below.
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Entry Name (optional)</label>
            <input
              value={entryName}
              onChange={e => setEntryName(e.target.value)}
              placeholder="e.g. Quotation for Acme Corp — Sept 2026"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="space-y-4 mb-6">
            {fields.map((field, i) => (
              <div key={i}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                {field.field_type === 'textarea' ? (
                  <textarea
                    value={fieldValues[field.field_key] || ''}
                    onChange={e => setFieldValues(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                ) : (
                  <input
                    type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                    value={fieldValues[field.field_key] || ''}
                    onChange={e => setFieldValues(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                )}
              </div>
            ))}
          </div>

          {lastSavedEntryId && (
            <div className="flex items-center gap-2 mb-4 text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm justify-between">
              <span>Quotation entry saved successfully.</span>
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {downloadingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Download PDF
              </button>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={resetAll} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-50">
              Upload Another Template
            </button>
            <button
              onClick={handleSaveEntry}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {lastSavedEntryId ? 'Save as New Entry' : 'Save Quotation Entry'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationTemplatePage;
